/**
 * Upload service for handling file uploads to storage
 */

import { api } from '../utils/api-client'
import { logger } from '@/lib/logger'

export interface UploadResponse {
  success: boolean
  /** Raw canonical S3 URL — persist this on the record */
  url?: string
  /** Presigned URL for immediate display (raw URL 403s on private buckets) */
  previewUrl?: string
  error?: string
}

export interface OnboardingUploadedFile {
  id: string
  originalName: string
  size: number
  type: string
  publicUrl: string
  s3Url: string
}

interface S3UploadApiFile {
  id: string
  originalName: string
  size: number
  type: string
  publicUrl: string
  s3Url: string
}

interface S3SingleUploadApiResponse {
  success: boolean
  data: S3UploadApiFile
}

/**
 * Upload an avatar file
 * @param file - The file to upload
 * @param workspaceId - The workspace ID for the upload path
 * @returns Promise with upload result
 */
export async function uploadAvatar(
  file: File,
  workspaceId: string
): Promise<UploadResponse> {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.'
      }
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size too large. Please upload an image smaller than 5MB.'
      }
    }

    // Create form data
    const formData = new FormData()
    formData.append('file', file)
    formData.append('workspaceId', workspaceId)

    // Upload file
    const response = await api.post('/upload/avatar', formData) as UploadResponse

    return response
  } catch (error: any) {
    logger.error('Avatar upload failed', error)
    return {
      success: false,
      error: error?.message || 'Upload failed. Please try again.'
    }
  }
}

/**
 * Delete an uploaded avatar file
 * @param url - The URL of the file to delete
 * @returns Promise with deletion result
 */
export async function deleteAvatar(url: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await api.delete('/upload/avatar', {
      body: { url }
    })

    return { success: response.success }
  } catch (error: any) {
    logger.error('Avatar deletion failed', error)
    return {
      success: false,
      error: error?.message || 'Deletion failed. Please try again.'
    }
  }
}

/**
 * Upload a chat attachment file to S3 storage.
 *
 * When `chatId` is supplied, the server registers the upload in the
 * `chat_attachments` table and returns a stable file id that the agent's
 * `read_attachments` tool can later resolve to S3 content. Without `chatId`
 * the upload still succeeds (legacy callers / non-chat surfaces still work),
 * but the returned id is a throw-away UUID that won't survive the request.
 *
 * @param file - The file to upload as a chat attachment
 * @param chatId - Current chat id; pass undefined for non-chat uploads
 * @returns Upload result with `publicUrl`, `fileId`, and full metadata
 */
export async function uploadChatAttachment(
  file: File,
  chatId?: string,
): Promise<{
  success: boolean
  publicUrl?: string
  fileId?: string
  data?: any
  error?: string
}> {
  try {
    // Create form data with file and category
    const formData = new FormData()
    formData.append('file', file)
    formData.append('category', 'attachment')
    if (chatId) {
      formData.append('chatId', chatId)
    }

    // Upload file using centralized API client
    const response = await api.post('/files/upload/s3', formData)

    return {
      success: true,
      publicUrl: response.publicUrl,
      fileId: response.data?.id,
      data: response.data
    }
  } catch (error: any) {
    logger.error('Chat attachment upload failed', error)
    return {
      success: false,
      error: error?.message || 'Upload failed. Please try again.'
    }
  }
}

/**
 * Upload onboarding documents to S3 storage
 * @param files - Files selected in the onboarding FileUploadZone
 * @returns Uploaded file references safe to store in onboarding data
 */
export async function uploadOnboardingFiles(
  files: File[]
): Promise<OnboardingUploadedFile[]> {
  const uploaded = await Promise.all(
    files.map(async (file) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', 'attachment')

      const response = await api.post<S3SingleUploadApiResponse>(
        '/files/upload/s3',
        formData
      )

      if (!response?.success || !response.data) {
        throw new Error(`Failed to upload ${file.name}`)
      }

      return {
        id: response.data.id,
        originalName: response.data.originalName,
        size: response.data.size,
        type: response.data.type,
        publicUrl: response.data.publicUrl,
        s3Url: response.data.s3Url,
      }
    })
  )

  return uploaded
}

/**
 * Delete an uploaded file from S3 storage
 * @param s3Url - The S3 URL of the file to delete
 * @returns Promise with deletion result
 */
export async function deleteFile(s3Url: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await api.delete('/files/s3', {
      body: { s3Url }
    })

    return { success: response.success }
  } catch (error: any) {
    logger.error('File deletion failed', error)
    return {
      success: false,
      error: error?.message || 'File deletion failed. Please try again.'
    }
  }
}
